import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs/Observable';
import { UserListModel } from '../models/UserListModel';
import { UserModel } from '../models/UserModel';
import { UserAccountDetailedStatusModel } from '../models/UserAccountDetailedStatusModel';
import { AddUserModel } from '../models/AddUserModel';
import { QueryResponse } from '../../core/services/QueryResponse';
import { ModelResponse } from '../../core/services/ModelResponse';
import { LookupModel } from '../../common/models/LookupModel';
import { RoleModel } from '../../security/models/RoleModel';
import { UserNameModel } from '../models/UserNameModel';
import { NotificationModel } from '../models/NotificationModel';
import { GroupListModel } from '../models/GroupListModel';

@Injectable()
export class UserService {
  constructor(public httpClient: HttpClient) {}

  getUserList(page: number, pageSize: number, sort: string, sortDirection: string, searchTerm: string): Observable<QueryResponse<UserListModel>> {
    let params = new HttpParams().set('skip', (page * pageSize).toString()).set('take', pageSize.toString());
    if (sort) {
      params = params.set('sort', sort).set('sortDirection', sortDirection);
    }
    if (searchTerm) {
      params = params.set('searchTerm', searchTerm);
    }
    return this.httpClient.get<QueryResponse<UserListModel>>('/api/CustomerModule/User/ListQuery', { params: params });
  }

  getGroupLookupsList(page: number, pageSize: number, sort: string, sortDirection: string, searchTerm: string): Observable<QueryResponse<UserListModel>> {
    let params = new HttpParams().set('skip', (page * pageSize).toString()).set('take', pageSize.toString());
    if (sort) {
      params = params.set('sort', sort).set('sortDirection', sortDirection);
    }
    if (searchTerm) {
      params = params.set('searchTerm', searchTerm);
    }
    return this.httpClient.get<QueryResponse<UserListModel>>('/api/CustomerModule/User/GroupListQuery', { params: params });
  }
  // TODO:move to common security service
  getAvailableRoles(page: number, pageSize: number, sort: string, sortDirection: string, searchTerm: string): Observable<QueryResponse<RoleModel>> {
    let params = new HttpParams().set('skip', (page * pageSize).toString()).set('take', pageSize.toString());
    if (sort) {
      params = params.set('sort', sort).set('sortDirection', sortDirection);
    }
    if (searchTerm) {
      params = params.set('searchTerm', searchTerm);
    }
    return this.httpClient.get<QueryResponse<RoleModel>>('/api/CustomerModule/User/AvailableRolesQuery', { params: params });
  }

  getUser(key: string): Observable<ModelResponse<UserModel>> {
    return this.httpClient.get<ModelResponse<UserModel>>('/api/CustomerModule/User/Get/' + key);
  }

  getCurrentUser(): Observable<ModelResponse<UserModel>> {
    return this.httpClient.get<ModelResponse<UserModel>>('/api/CustomerModule/User/GetCurrentUser/');
  }

  GetUserCurrentStatus(userKey: string): Observable<ModelResponse<UserAccountDetailedStatusModel>> {
    return this.httpClient.get<ModelResponse<UserAccountDetailedStatusModel>>(`/api/CustomerModule/User/Get/${userKey}/CurrentStatus/`);
  }

  getCurrentUserSubmittingEntities(groupsOnly: boolean = true): Observable<ModelResponse<LookupModel[]>> {
    const params = new HttpParams().set('groupsOnly', groupsOnly.toString());
    return this.httpClient.get<ModelResponse<LookupModel[]>>('/api/CustomerModule/User/CurrentUserSubmittingEntities/', { params: params });
  }

  getCurrentUserUsers(): Observable<ModelResponse<LookupModel[]>> {
    return this.httpClient.get<ModelResponse<LookupModel[]>>('/api/CustomerModule/User/CurrentUserUsers/');
  }

  getUserAssociatedSubmittingEntities(userKey: string): Observable<ModelResponse<LookupModel[]>> {
    return this.httpClient.get<ModelResponse<LookupModel[]>>(`/api/CustomerModule/User/${userKey}/AssociatedSubmittingEntities/`);
  }

  getNotificationTypes(): Observable<ModelResponse<LookupModel[]>> {
    return this.httpClient.get<ModelResponse<LookupModel[]>>('/api/CustomerModule/User/NotificationTypes/');
  }

  addUser(addModel: AddUserModel): Observable<ModelResponse<string>> {
    return this.httpClient.put<ModelResponse<string>>('/api/CustomerModule/User/Add/', addModel);
  }

  resetPassword(userKey: string, identifier: string): Observable<ModelResponse<boolean>> {
    return this.httpClient.post<ModelResponse<boolean>>(`/api/CustomerModule/User/${userKey}/ResetPassword/`, { model: identifier });
  }

  lockAccount(userKey: string, reason: string): Observable<ModelResponse<string>> {
    return this.httpClient.post<ModelResponse<string>>(`/api/CustomerModule/User/${userKey}/LockAccount/`, { lockReason: reason });
  }

  disableAccount(userKey: string): Observable<ModelResponse<string>> {
    return this.httpClient.post<ModelResponse<string>>(`/api/CustomerModule/User/${userKey}/DisableAccount/`, '');
  }

  unlockAccount(userKey: string): Observable<ModelResponse<string>> {
    return this.httpClient.post<ModelResponse<string>>(`/api/CustomerModule/User/${userKey}/UnlockAccount/`, '');
  }

  enableAccount(userKey: string): Observable<ModelResponse<string>> {
    return this.httpClient.post<ModelResponse<string>>(`/api/CustomerModule/User/${userKey}/EnableAccount/`, '');
  }

  addAllGroups(userKey: string, allowAllGroups: boolean, lookUpModels:LookupModel[]): Observable<ModelResponse<LookupModel[]>> {
    return this.httpClient.post<ModelResponse<LookupModel[]>>('/api/CustomerModule/User/' + userKey + '/Group/'+ allowAllGroups,lookUpModels);
  }

  addGroup(userKey: string, model: LookupModel): Observable<ModelResponse<LookupModel>> {
    return this.httpClient.put<ModelResponse<LookupModel>>('/api/CustomerModule/User/' + userKey + '/Group/', model);
  }

  removeGroup(userKey: string, model: LookupModel): Observable<ModelResponse<LookupModel>> {
    return this.httpClient.post<ModelResponse<LookupModel>>('/api/CustomerModule/User/' + userKey + '/Group/', model);
  }

  addNotification(userKey: string, model: NotificationModel): Observable<ModelResponse<NotificationModel>> {
    return this.httpClient.put<ModelResponse<NotificationModel>>('/api/CustomerModule/User/' + userKey + '/Notification/', model);
  }

  removeNotification(userKey: string, model: NotificationModel): Observable<ModelResponse<NotificationModel>> {
    return this.httpClient.post<ModelResponse<NotificationModel>>('/api/CustomerModule/User/' + userKey + '/Notification/', model);
  }

  addRole(userKey: string, model: RoleModel): Observable<ModelResponse<RoleModel>> {
    return this.httpClient.put<ModelResponse<RoleModel>>('/api/CustomerModule/User/' + userKey + '/Role/', model);
  }

  removeRole(userKey: string, roleKey: string): Observable<ModelResponse<boolean>> {
    return this.httpClient.delete<ModelResponse<boolean>>('/api/CustomerModule/User/' + userKey + '/Role/' + roleKey);
  }

  reviseName(userKey: string, model: UserNameModel): Observable<ModelResponse<UserNameModel>> {
    return this.httpClient.post<ModelResponse<UserNameModel>>('/api/CustomerModule/User/' + userKey + '/ReviseName/', model);
  }

  reviseEmailAddress(userKey: string, emailAddress: string): Observable<ModelResponse<string>> {
    return this.httpClient.post<ModelResponse<string>>('/api/CustomerModule/User/' + userKey + '/ReviseEmail/', { model: emailAddress });
  }

  globalLock(): Observable<ModelResponse<string>> {
    return this.httpClient.post<ModelResponse<string>>(`/api/CustomerModule/User/GlobalLock/`, '');
  }

  globalUnlock(): Observable<ModelResponse<string>> {
    return this.httpClient.post<ModelResponse<string>>(`/api/CustomerModule/User/GlobalUnlock/`, '');
  }

  checkGlobalLock(): Observable<ModelResponse<boolean>> {
    return this.httpClient.get<ModelResponse<boolean>>(`/api/CustomerModule/User/CheckGlobalLock/`);
  }
}



// WEBPACK FOOTER //
// ./src/app/customer/services/UserService.ts